package com.eladfinish.bible.contest.extensions

import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.em

// ─── Single-transformation extensions ────────────────────────────────────────

/** *text* → bold, asterisks removed. Mirrors makeBoldByAsterisks(). */
fun String.makeBoldByAsterisksAnnotated(): AnnotatedString =
    toAnnotatedString().makeBoldByAsterisksAnnotated()

fun AnnotatedString.makeBoldByAsterisksAnnotated(): AnnotatedString {
    val regex = "\\*.*?\\*".toRegex()
    return buildAnnotatedString {
        var cursor = 0
        for (match in regex.findAll(this@makeBoldByAsterisksAnnotated)) {
            append(this@makeBoldByAsterisksAnnotated.subSequence(cursor, match.range.first))
            withStyle(SpanStyle(fontWeight = FontWeight.Bold)) {
                append(this@makeBoldByAsterisksAnnotated.subSequence(match.range.first + 1, match.range.last))
            }
            cursor = match.range.last + 1
        }
        append(this@makeBoldByAsterisksAnnotated.subSequence(cursor, this@makeBoldByAsterisksAnnotated.length))
    }
}

/** <b>text</b> → bold, tags removed. Mirrors makeBoldByHtmlTags(). */
fun AnnotatedString.makeBoldByHtmlTagsAnnotated(): AnnotatedString =
    replaceTagsWithStyle("<b>", "</b>", SpanStyle(fontWeight = FontWeight.Bold))

/** <big>text</big> → 1.3× font size, tags removed. Mirrors increaseTextSizeByTags(). */
fun AnnotatedString.increaseTextSizeByTagsAnnotated(): AnnotatedString =
    replaceTagsWithStyle("<big>", "</big>", SpanStyle(fontSize = 1.3.em))

/** <small>text</small> → 0.7× font size, tags removed. Mirrors decreaseTextSizeByTags(). */
fun AnnotatedString.decreaseTextSizeByTagsAnnotated(): AnnotatedString =
    replaceTagsWithStyle("<small>", "</small>", SpanStyle(fontSize = 0.7.em))

/**
 * Sequences of underscores (_+) → serif font, matching the legacy
 * makeContinuousUnderlined() which applies TypefaceSpan(FONT_SERIF).
 */
fun AnnotatedString.makeContinuousUnderlinedAnnotated(): AnnotatedString {
    val regex = "_+".toRegex()
    return buildAnnotatedString {
        var cursor = 0
        for (match in regex.findAll(this@makeContinuousUnderlinedAnnotated)) {
            append(this@makeContinuousUnderlinedAnnotated.subSequence(cursor, match.range.first))
            withStyle(SpanStyle(fontFamily = FontFamily.Serif)) {
                append(match.value)
            }
            cursor = match.range.last + 1
        }
        append(this@makeContinuousUnderlinedAnnotated.subSequence(cursor, this@makeContinuousUnderlinedAnnotated.length))
    }
}

// ─── Convenience: apply the full question-text styling chain ─────────────────

/**
 * Applies the same styling chain as the legacy CustomListAdapterBase.setDataInViewHolder
 * for the question/answer text:
 *   increaseTextSizeByTags → decreaseTextSizeByTags → makeBoldByHtmlTags
 *   → makeBoldByAsterisks → makeContinuousUnderlined
 */
fun String.applyQuestionMarkup(): AnnotatedString =
    toAnnotatedString()
        .increaseTextSizeByTagsAnnotated()
        .decreaseTextSizeByTagsAnnotated()
        .makeBoldByHtmlTagsAnnotated()
        .makeBoldByAsterisksAnnotated()
        .makeContinuousUnderlinedAnnotated()

/**
 * Builds the seyfa (answer) AnnotatedString.
 * - Applies full question markup
 * - Appends the author attribution in smaller (0.75×) italic, matching the
 *   legacy RelativeSizeSpan(0.75f) + StyleSpan(ITALIC) in setDataInViewHolder
 */
fun String.applyAnswerMarkup(author: String): AnnotatedString {
    val base = applyQuestionMarkup()
    if (author.isEmpty()) return base

    val authorLabel = if (listOf("סימן", "פסוק", "#").any { author.startsWith(it) }) {
        "  [$author]"
    } else {
        "  (תודה ל$author)"
    }

    return buildAnnotatedString {
        append(base)
        withStyle(
            SpanStyle(
                fontSize = 0.75.em,
                fontStyle = FontStyle.Italic,
            )
        ) {
            append(authorLabel)
        }
    }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

private fun String.toAnnotatedString() = AnnotatedString(this)

private fun AnnotatedString.replaceTagsWithStyle(
    openTag: String,
    closeTag: String,
    style: SpanStyle,
): AnnotatedString {
    val regex = "${Regex.escape(openTag)}[\\s\\S]*?${Regex.escape(closeTag)}".toRegex()
    return buildAnnotatedString {
        var cursor = 0
        for (match in regex.findAll(this@replaceTagsWithStyle)) {
            append(this@replaceTagsWithStyle.subSequence(cursor, match.range.first))
            val inner = match.value.substring(openTag.length, match.value.length - closeTag.length)
            withStyle(style) { append(inner) }
            cursor = match.range.last + 1
        }
        append(this@replaceTagsWithStyle.subSequence(cursor, this@replaceTagsWithStyle.length))
    }
}
